import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GoogleIntegrationService } from '../../../../services/google-integration.service';
import GoogleIntegrationDiagnostic from '../../../../shared/models/google/google-integration-diagnostic';
import GoogleIntegrationDiagnosticIssue from '../../../../shared/models/google/google-integration-diagnostic-issue';
import { IntegrationIssues } from '../../../../shared/models/google/integration-issues';
import { catchError, exhaustMap, finalize, first, forkJoin, of, switchMap, interval, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const TIME_BETWEEN_DIAGNOSTICS = 60 * 60 * 1000;
const DIAGNOSTIC_POLL_INTERVAL = 10 * 1000;
const HELP_TEXT_KEY_PREFIX = 'GoogleIntegrationDiagnostics_';

@Component({
  selector: 'app-google-integration-diagnostic-panel',
  templateUrl: './google-integration-diagnostic-panel.component.html'
})
export class GoogleIntegrationDiagnosticPanelComponent implements OnInit {
  @Input() customerId!: number;
  // The hourly cooldown is enforced in the UI. Task workspaces skip it so the specialist can re-run after a fix.
  @Input() taskId: number | null = null;
  @Input() isReadOnly = false;
  @Output() diagnosticCompleted = new EventEmitter<GoogleIntegrationDiagnostic>();

  diagnosticIssues: GoogleIntegrationDiagnosticIssue[] = [];
  googleIntegrationDiagnostic: GoogleIntegrationDiagnostic | null = null;
  correctionTriggered = false;
  hasFreshDiagnostic = false;
  hasLoadError = false;
  isCorrectionInProgress = false;
  isLoading = false;

  constructor(
    private destroyRef: DestroyRef,
    private googleIntegrationService: GoogleIntegrationService
  ) { }

  get isTaskContext(): boolean {
    return this.taskId !== null;
  }

  get isInCooldownPeriod(): boolean {
    return !this.isTaskContext && this.hasFreshDiagnostic;
  }

  get unresolvedIssueCount(): number {
    return this.diagnosticIssues.filter(issue => !issue.WasResolved).length;
  }

  get unresolvedIssueSummary(): string {
    return this.unresolvedIssueCount === 1
      ? '1 issue could not be corrected automatically.'
      : `${this.unresolvedIssueCount} issues could not be corrected automatically.`;
  }

  ngOnInit(): void {
    this.isLoading = true;
    forkJoin([
      this.googleIntegrationService.getLatestDiagnostic(this.customerId),
      this.googleIntegrationService.isIntegrationCorrectionInProgress(this.customerId)
    ])
      .pipe(
        finalize(() => this.isLoading = false),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: ([diagnostic, isCorrectionInProgress]) => {
          this.setDiagnostic(diagnostic);
          this.isCorrectionInProgress = isCorrectionInProgress;

          if (this.isCorrectionInProgress) {
            this.correctionTriggered = true;
            this.pollDiagnosticCompletion();
          }
        },
        error: error => {
          this.hasLoadError = true;
          toastr.error(error);
        }
      });
  }

  correctIntegration(): void {
    this.correctionTriggered = true;
    this.isCorrectionInProgress = true;
    this.startCorrection()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.pollDiagnosticCompletion();
        },
        error: error => {
          toastr.error(error);
          this.correctionTriggered = false;
          this.isCorrectionInProgress = false;
        }
      });
  }

  getHelpTextKey(diagnosticIssue: GoogleIntegrationDiagnosticIssue): string {
    return `${HELP_TEXT_KEY_PREFIX}${IntegrationIssues[diagnosticIssue.GoogleIntegrationIssueId]}`;
  }

  private startCorrection(): Observable<void> {
    return this.isTaskContext
      ? this.googleIntegrationService.correctIntegrationForTask(this.customerId, this.taskId)
      : this.googleIntegrationService.correctIntegration(this.customerId);
  }

  private pollDiagnosticCompletion(): void {
    interval(DIAGNOSTIC_POLL_INTERVAL)
      .pipe(
        exhaustMap(() => this.googleIntegrationService.isIntegrationCorrectionInProgress(this.customerId)
          .pipe(catchError(() => of(true)))),
        first(isCorrectionInProgress => !isCorrectionInProgress),
        switchMap(() => this.googleIntegrationService.getLatestDiagnostic(this.customerId)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: diagnostic => {
          if (diagnostic?.GoogleIntegrationDiagnosticId !== this.googleIntegrationDiagnostic?.GoogleIntegrationDiagnosticId) {
            this.setDiagnostic(diagnostic);
            this.diagnosticCompleted.emit(diagnostic);
          } else {
            toastr.warning('Diagnostic failed.');
          }

          this.isCorrectionInProgress = false;
        },
        error: error => {
          toastr.error(error);
          this.isCorrectionInProgress = false;
        }
      });
  }

  private setDiagnostic(diagnostic: GoogleIntegrationDiagnostic | null): void {
    this.googleIntegrationDiagnostic = diagnostic;
    this.diagnosticIssues = diagnostic?.GoogleIntegrationDiagnosticIssues ?? [];
    this.hasFreshDiagnostic = !!diagnostic && Date.now() - TIME_BETWEEN_DIAGNOSTICS < new Date(diagnostic.InsertedDate).getTime();
  }
}
